import { type CoursePreview, ExamDifficulty } from "@brookbush/types";

import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import { FaCheckCircle } from "react-icons/fa";

import usePassedCourses from "~/hooks/usePassedCourses";
import { examDifficultyMap } from "~/utils/constants";

interface CoursesCardProps extends CoursePreview {
	isRecommended?: boolean;
}

const CoursesCard = ({
	slug,
	title,
	thumbnail,
	_id,
	credits,
	isRecommended,
	finalExamDifficulty,
}: CoursesCardProps) => {
	const { passedCourses } = usePassedCourses();

	const isPassed = passedCourses?.some((course) => course.courseId === _id);

	const examDifficulty = examDifficultyMap[finalExamDifficulty];

	return (
		<Link
			href={`/courses/${slug}`}
			className={clsx(
				"course-card group relative mx-auto flex phablet:aspect-[5/3] phablet:max-w-full cursor-pointer phablet:flex-col overflow-hidden phablet:rounded-2xl rounded-xl border phablet:border bg-white transition-all phablet:duration-300 phablet:hover:scale-105 hover:bg-slate-50 phablet:hover:shadow-xl dark:bg-slate-800 dark:shadow-slate-900 dark:hover:bg-slate-700",
				{
					"shadow-[0_0_20px_2px_rgba(251,146,60,0.45)] ring-2 ring-orange-400":
						isRecommended,
				},
			)}
		>
			<div className="phablet:absolute relative aspect-square h-20 phablet:h-full phablet:w-full w-20 shrink-0 overflow-hidden border-r tablet:border-r-0 transition-all duration-700">
				<Image
					src={thumbnail?.url || "/images/placeholder.png"}
					fill
					placeholder="empty"
					className="object-cover"
					alt="picture"
				/>

				{isPassed && (
					<div className="absolute top-0 left-0 flex phablet:hidden h-full w-full items-center justify-center bg-emerald-900/30 backdrop-blur dark:bg-slate-800/60">
						<FaCheckCircle
							size={30}
							className="text-emerald-400 dark:text-emerald-400"
						/>
					</div>
				)}
			</div>

			<div
				className={clsx(
					"absolute top-0 left-0 phablet:flex hidden h-full w-full flex-col items-start justify-end gap-2.5 bg-gradient-to-t to-transparent px-3.5 py-2.5",
					isPassed ? "from-emerald-950" : `from-black/90`,
				)}
			>
				<h3 className={clsx(`line-clamp font-semibold text-lg text-white`)}>
					{title}
				</h3>
			</div>

			<div className="flex flex-col gap-0 px-2.5 py-1">
				<div className="phablet:absolute phablet:top-3 right-3 left-3 flex gap-2">
					<p
						className={clsx(
							"phablet:block phablet:rounded-full phablet:bg-slate-50 phablet:px-2 phablet:py-1 font-semibold phablet:text-slate-700 text-xs phablet:shadow-xl phablet:dark:text-slate-800 phablet:dark:shadow-black/20",
						)}
					>
						{credits} {credits === 1 ? "Credit" : "Credits"}
						{examDifficulty && (
							<span
								className={clsx(
									`ml-1.5 border-slate-300 border-l pl-1.5 dark:border-slate-500`,

									finalExamDifficulty === ExamDifficulty.UNRATED
										? "text-slate-500"
										: finalExamDifficulty === ExamDifficulty.VERY_EASY
											? "text-sky-700"
											: finalExamDifficulty === ExamDifficulty.EASY
												? "text-sky-700"
												: finalExamDifficulty === ExamDifficulty.MEDIUM
													? "text-orange-700"
													: finalExamDifficulty === ExamDifficulty.HARD
														? "0 text-red-700"
														: "text-purple-700",
								)}
							>
								{examDifficulty.title}
							</span>
						)}
					</p>

					{isRecommended && !isPassed && (
						<p
							className={clsx(
								"phablet:ml-auto flex phablet:flex items-center gap-0.5 phablet:rounded-full border-l phablet:border-none phablet:bg-orange-400 phablet:px-2 phablet:py-1 pl-2 font-semibold phablet:text-white text-orange-700 text-xs phablet:shadow-xl dark:text-orange-300 phablet:dark:text-white",
							)}
						>
							Recommended
						</p>
					)}

					{isPassed && (
						<p
							className={clsx(
								"phablet:ml-auto flex phablet:flex items-center gap-0.5 phablet:rounded-full border-l phablet:border-none phablet:bg-emerald-300 phablet:px-2 phablet:py-1 pl-2 font-semibold phablet:text-slate-700 text-emerald-700 text-xs phablet:shadow-xl phablet:dark:bg-emerald-300 dark:text-emerald-300 phablet:dark:text-slate-700 phablet:dark:saturate-75",
							)}
						>
							Passed
						</p>
					)}
				</div>

				<h3 className="line-clamp-2 phablet:hidden font-medium">{title}</h3>
			</div>
		</Link>
	);
};

export default CoursesCard;
